import { Calendar, AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import type {
  IdentityFieldSummary,
  IdentitySourceOptions,
  IdentityValueMapWorkspace,
  IdentityWorkspaceMode,
} from '@/types/components/audiences/import-session'
import type { InferenceDistributionEntry } from '@/types/components/audiences/inferred-mix-editor'
import { cn } from '@/lib/utils'
import {
  FIELD_ICONS,
  formatFieldStateLabel,
  getModeLabel,
  getStateBadgeVariant,
  identityFieldLabel,
} from '../lib/required-fields-ui'
import { IdentityConfirmSourcePanel } from './IdentityConfirmSourcePanel'
import { IdentityInferencePanel } from './IdentityInferencePanel'
import { IdentitySetConstantPanel } from './IdentitySetConstantPanel'
import { IdentityValueMapPanel } from './IdentityValueMapPanel'

export interface IdentityWorkspaceInlineProps {
  activeField: IdentityFieldSummary | null
  availableModes: IdentityWorkspaceMode[]
  currentMode: IdentityWorkspaceMode | null
  allFieldsSatisfied: boolean
  actionError: string | null
  showWorkspaceRefreshIndicator: boolean
  showWorkspaceLoader: boolean
  isPatching: boolean
  isLoadingMore?: boolean
  mappingBusy: boolean
  sourceOptions: IdentitySourceOptions | null
  valueMapWorkspace: IdentityValueMapWorkspace | null
  draftSourceColumn: string
  sourceSearch: string
  sourceDropdownOpen: boolean
  draftConstantValue: string
  workspaceDirty: boolean
  draftDistribution: InferenceDistributionEntry[] | null
  canSubmitConfirmSource: boolean
  /**
   * When `currentMode === 'map_values'`, controls the source-column submit row
   * (first-time "Map to question" vs "Use this column" remap).
   */
  mapValuesConfirmSourceSubmit?: {
    hideButton: boolean
    canSubmit: boolean
    submitLabel: string
    submitHint: string | null
  }
  /** When true, used inside an accordion row (lighter wrapper, no duplicate header). */
  nested?: boolean
  onFocusMode: (mode: IdentityWorkspaceMode) => void
  onDraftSourceColumnChange: (value: string) => void
  onSourceSearchChange: (value: string) => void
  onLoadMore: () => void
  onDropdownClose: () => void
  onSourceDropdownOpenChange: (open: boolean) => void
  onDraftConstantValueChange: (value: string) => void
  onDraftDistributionChange: (value: InferenceDistributionEntry[] | null) => void
  onMappingDirtyChange: (dirty: boolean) => void
  onMappingBusyChange: (busy: boolean) => void
  onConfirmSource: () => void
  onSetConstant: () => void
  onAcceptInferred: () => void
  onApplyValueMap: (sourceColumn: string, valueMap: Record<string, string>) => void
  onUndoConstant?: () => void
}

/**
 * Inline identity field workspace used by step 3 Define identity fields.
 */
export function IdentityWorkspaceInline({
  activeField,
  availableModes,
  currentMode,
  allFieldsSatisfied,
  actionError,
  showWorkspaceRefreshIndicator,
  showWorkspaceLoader,
  isPatching,
  isLoadingMore = false,
  mappingBusy,
  sourceOptions,
  valueMapWorkspace,
  draftSourceColumn,
  sourceSearch,
  sourceDropdownOpen,
  draftConstantValue,
  workspaceDirty,
  draftDistribution,
  canSubmitConfirmSource,
  mapValuesConfirmSourceSubmit,
  onFocusMode,
  onDraftSourceColumnChange,
  onSourceSearchChange,
  onLoadMore,
  onDropdownClose,
  onSourceDropdownOpenChange,
  onDraftConstantValueChange,
  onDraftDistributionChange,
  onMappingDirtyChange,
  onMappingBusyChange,
  onConfirmSource,
  onSetConstant,
  onAcceptInferred,
  onApplyValueMap,
  onUndoConstant,
  nested = false,
}: IdentityWorkspaceInlineProps) {
  const { t } = useTranslation('app')
  if (!activeField) {
    return null
  }

  const fieldLabel = identityFieldLabel(activeField.canonicalId, activeField.label, t)
  const Icon = FIELD_ICONS[activeField.canonicalId] ?? Calendar

  return (
    <section
      className={cn(
        'flex flex-col gap-4',
        !nested && 'rounded-xl border border-border/70 bg-card/80 p-5',
      )}
      aria-labelledby={nested ? undefined : 'identity-workspace-heading'}
    >
      {!nested ? (
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-dark/80">
              <Icon className="h-[18px] w-[18px] text-muted-foreground" />
            </div>
            <div className="flex items-center gap-2">
              <h2
                id="identity-workspace-heading"
                className="text-lg font-semibold text-foreground"
              >
                {fieldLabel}
              </h2>
              <Badge variant={getStateBadgeVariant(activeField.state)}>
                {formatFieldStateLabel(activeField.state)}
              </Badge>
            </div>
          </div>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t('audienceCreate.defineFieldForAudience', {
              field: fieldLabel.toLowerCase(),
            })}
          </p>
        </div>
      ) : null}

      {actionError ? (
        <Alert variant="destructive" className="rounded-xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      ) : null}

      {!allFieldsSatisfied ? (
        <div
          role="tablist"
          aria-label={t('audienceCreate.definitionModeAria', { field: fieldLabel })}
          className="inline-flex items-center gap-1 self-start rounded-lg border border-border bg-muted p-1"
        >
          {availableModes
            .filter((mode) => mode !== 'map_values')
            .map((mode) => {
              const isActive =
                currentMode === mode ||
                (mode === 'confirm_source' && currentMode === 'map_values')
              return (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  disabled={
                    isPatching || (mappingBusy && currentMode !== mode)
                  }
                  onClick={() => onFocusMode(mode)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium tracking-tight transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    isActive
                      ? 'bg-background text-foreground shadow-sm dark:bg-card dark:text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {getModeLabel(mode)}
                </button>
              )
            })}
        </div>
      ) : null}

      {showWorkspaceRefreshIndicator ? (
        <div
          aria-live="polite"
          className="flex items-center gap-2 px-1 text-xs text-muted-foreground"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Refreshing workspace
        </div>
      ) : null}

      <div className="min-h-[120px]">
        {showWorkspaceLoader ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading field workspace…
          </div>
        ) : (
          <>
            {currentMode === 'confirm_source' ? (
              <IdentityConfirmSourcePanel
                sourceOptions={sourceOptions}
                draftSourceColumn={draftSourceColumn}
                sourceSearch={sourceSearch}
                sourceDropdownOpen={sourceDropdownOpen}
                isPatching={isPatching}
                isLoadingMore={isLoadingMore}
                canSubmit={canSubmitConfirmSource}
                onDraftSourceColumnChange={onDraftSourceColumnChange}
                onSourceSearchChange={onSourceSearchChange}
                onLoadMore={onLoadMore}
                onDropdownClose={onDropdownClose}
                onSourceDropdownOpenChange={onSourceDropdownOpenChange}
                onSubmit={onConfirmSource}
              />
            ) : null}

            {currentMode === 'map_values' ? (
              <div className="space-y-6">
                <IdentityConfirmSourcePanel
                  sourceOptions={sourceOptions}
                  draftSourceColumn={draftSourceColumn}
                  sourceSearch={sourceSearch}
                  sourceDropdownOpen={sourceDropdownOpen}
                  isPatching={isPatching}
                  isLoadingMore={isLoadingMore}
                  canSubmit={mapValuesConfirmSourceSubmit?.canSubmit ?? false}
                  onDraftSourceColumnChange={onDraftSourceColumnChange}
                  onSourceSearchChange={onSourceSearchChange}
                  onLoadMore={onLoadMore}
                  onDropdownClose={onDropdownClose}
                  onSourceDropdownOpenChange={onSourceDropdownOpenChange}
                  onSubmit={onConfirmSource}
                  hideButton={mapValuesConfirmSourceSubmit?.hideButton ?? false}
                  submitButtonLabel={
                    mapValuesConfirmSourceSubmit?.submitLabel ?? t('audienceCreate.mapToQuestion')
                  }
                  submitHint={mapValuesConfirmSourceSubmit?.submitHint ?? null}
                  readOnly={isPatching}
                />
                <IdentityValueMapPanel
                  field={activeField}
                  workspace={valueMapWorkspace}
                  isPatching={isPatching}
                  onDirtyChange={onMappingDirtyChange}
                  onBusyChange={onMappingBusyChange}
                  onApply={onApplyValueMap}
                />
              </div>
            ) : null}

            {currentMode === 'set_constant' ? (
              <IdentitySetConstantPanel
                field={activeField}
                draftConstantValue={draftConstantValue}
                workspaceDirty={workspaceDirty}
                isPatching={isPatching}
                onDraftConstantValueChange={onDraftConstantValueChange}
                onSubmit={onSetConstant}
                onUndo={onUndoConstant}
              />
            ) : null}

            {currentMode === 'review_inference' ? (
              <IdentityInferencePanel
                field={activeField}
                draftDistribution={draftDistribution}
                isPatching={isPatching}
                onDraftDistributionChange={onDraftDistributionChange}
                onSubmit={onAcceptInferred}
                onUndo={() => {
                  const values = activeField.suggestedInference?.values
                  onDraftDistributionChange(
                    values?.map((v) => ({
                      value: v.value,
                      percentage: v.percentage,
                    })) ?? null,
                  )
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </section>
  )
}
